'use client'

import type { Data } from '@repo/strapi'
import AppLink from '@/components/elementary/AppLink'
import { CTAButtons } from '@/components/elementary/navbar/desktop/CTAButtons'
import { Paragraph } from '@/components/typography/Paragraph'
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { NAVBAR_TEST_IDS } from '@/lib/test-ids'
import { CategoryColumn } from '../CategoryColumn'
import { FeaturedCard } from '../FeaturedCard'
import { SeoNavigationFallback } from '../SeoNavigationFallback'

interface DesktopNavigationProps {
	navbarItems?: Data.Component<'layout-utilities.navbar-item'>[] | null
	logInLink?: Data.Component<'utilities.link'> | null
	primaryButtons?: Data.Component<'utilities.link'>[] | null
	dropdownItem?: Data.Component<'layout-utilities.dropdown-item'> | null
	knockAiId?: string | null
	minimal?: boolean
}

export function DesktopNavigation({
	navbarItems,
	logInLink,
	primaryButtons,
	dropdownItem,
	knockAiId,
	minimal,
}: DesktopNavigationProps) {
	return (
		<>
			{/* SEO fallback navigation - screen reader only */}
			<SeoNavigationFallback
				navbarItems={navbarItems}
				logInLink={logInLink}
				primaryButtons={primaryButtons}
				dropdownItem={dropdownItem}
			/>

			{/* Main visible navigation */}
			<NavigationMenu viewport={false} className="desktop-nav">
				<NavigationMenuList
					className="hidden lg:flex gap-8 items-center h-5.5"
					data-test-id={NAVBAR_TEST_IDS.NAVIGATION_ITEMS}
				>
					{/* Main navigation items */}
					{Array.isArray(navbarItems) &&
						navbarItems.map((item) => (
							<NavigationMenuItem key={item.id}>
								{item.href ? (
									<AppLink
										href={item.href}
										variant="primaryLink"
										data-test-id={NAVBAR_TEST_IDS.NAVIGATION_ITEM}
										openExternalInNewTab={item.newTab ?? false}
									>
										<Paragraph fontSize="bodyRegularMedium" className="text-inherit">
											{item.label}
										</Paragraph>
									</AppLink>
								) : (
									<NavigationMenuTrigger
										data-test-id={NAVBAR_TEST_IDS.NAVIGATION_ITEM}
										className={navigationMenuTriggerStyle()}
									>
										<Paragraph fontSize="bodyRegularMedium" className="text-inherit">
											{item.label}
										</Paragraph>
									</NavigationMenuTrigger>
								)}

								{(!!item.categoryCards?.length || !!item.featuredCards?.length) && (
									<NavigationMenuContent className="lg:left-1/6 lg:-translate-x-1/6 xl:left-0 xl:translate-0 z-20">
										<ul className="bg-neutral-light-200 rounded-sm p-4 flex gap-4">
											{item.categoryCards?.map((categoryCard) => (
												<li
													key={`categoryCardItem-${categoryCard.id}`}
													className="flex gap-4 bg-neutral-light-100 rounded-sm p-4"
												>
													{categoryCard?.column?.map(
														(oneColumn: Data.Component<'layout-utilities.column-section'>) => (
															<CategoryColumn
																key={`categoryCard-${oneColumn.id}`}
																column={oneColumn}
																testId={NAVBAR_TEST_IDS.CATEGORY_COLUMN}
															/>
														),
													)}
												</li>
											))}

											{item.featuredCards?.map(
												(featuredCard: Data.Component<'layout-utilities.featured-card'>) => (
													<li key={`featureCardItem-${featuredCard.id}`}>
														<FeaturedCard card={featuredCard} />
													</li>
												),
											)}
										</ul>
									</NavigationMenuContent>
								)}
							</NavigationMenuItem>
						))}

					{/* CTA Buttons & Dropdown menu */}
					<CTAButtons
						logInLink={logInLink}
						primaryButtons={primaryButtons}
						dropdownItem={dropdownItem}
						knockAiId={knockAiId}
						minimal={minimal}
					/>
				</NavigationMenuList>
			</NavigationMenu>
		</>
	)
}
